import { useTranslation } from 'react-i18next';
import type { TourDefinition } from '@klonup/design-system';

/**
 * Tour interactivo "Crear conexión" — Admin-only. Estrena la capacidad `advanceOn` del motor:
 * el paso `open` avanza cuando el usuario pulsa de verdad "Añadir conexión" (no con "Siguiente").
 * Los pasos `name`/`apikey` apuntan a campos del drawer, que solo existen tras abrirlo — el
 * MutationObserver del motor los localiza cuando aparecen.
 *
 * Primer paso `intro` SIN target (centrado): da tiempo a que la página termine de cargar y el
 * layout se asiente (colapsar paneles / cerrar drawers) mientras el usuario lee, antes de que
 * el paso `open` resalte un botón concreto. Evita que el spotlight aparezca descolocado.
 *
 * `requiredPath: 'my-organization'`: las anclas `data-tour` solo existen en MyOrganizationPage.
 * El motor del DS no navega — OrgLayout navega a esta ruta antes de arrancar (startTourWithNav).
 */
export function useCreateConnectionTour(): TourDefinition {
  const { t } = useTranslation();
  return {
    id: 'create-connection',
    title: t('onboarding.create_connection.menuTitle'),
    description: t('onboarding.create_connection.menuDescription'),
    icon: '➕',
    requiredPath: 'my-organization',
    steps: [
      {
        id: 'intro',
        // Sin target → modal centrado de bienvenida. Mientras se lee, el layout se asienta.
        title: t('onboarding.create_connection.steps.intro.title'),
        description: t('onboarding.create_connection.steps.intro.description'),
      },
      {
        id: 'open',
        target: '[data-tour="new-connection-button"]',
        // Avanza cuando el usuario pulsa de verdad el botón (advanceOn click sobre el target).
        advanceOn: { on: 'click' },
        title: t('onboarding.create_connection.steps.open.title'),
        description: t('onboarding.create_connection.steps.open.description'),
      },
      {
        id: 'name',
        target: '[data-tour="connection-form-name"]',
        title: t('onboarding.create_connection.steps.name.title'),
        description: t('onboarding.create_connection.steps.name.description'),
      },
      {
        id: 'config',
        target: '[data-tour="connection-form-config"]',
        title: t('onboarding.create_connection.steps.config.title'),
        description: t('onboarding.create_connection.steps.config.description'),
      },
      {
        id: 'apikey_help',
        // SIN target → se renderiza como modal centrado y ancho (variante is-wide del popover),
        // para que el carrusel de capturas se vea grande de primeras. El paso `apikey` siguiente
        // ya resalta el campo concreto; aquí el foco es enseñar cómo obtener la key en Smartsheet.
        title: t('onboarding.create_connection.steps.apikey_help.title'),
        description: t('onboarding.create_connection.steps.apikey_help.description'),
        // Carrusel de capturas (recortadas a la zona relevante) de cómo obtener la API key en
        // Smartsheet. Auto-avanza en bucle. Las imágenes viven en GCS (bucket klonup-images).
        media: {
          type: 'carousel',
          label: t('onboarding.create_connection.steps.apikey_help.video_label'),
          images: [
            'https://storage.googleapis.com/klonup-images/onboarding/apikey-1.png',
            'https://storage.googleapis.com/klonup-images/onboarding/apikey-2.png',
            'https://storage.googleapis.com/klonup-images/onboarding/apikey-3.png',
            'https://storage.googleapis.com/klonup-images/onboarding/apikey-4.png',
            'https://storage.googleapis.com/klonup-images/onboarding/apikey-5.png',
          ],
        },
      },
      {
        id: 'apikey',
        target: '[data-tour="connection-form-apikey"]',
        title: t('onboarding.create_connection.steps.apikey.title'),
        description: t('onboarding.create_connection.steps.apikey.description'),
      },
      {
        id: 'done',
        // Sin target → se renderiza como modal centrado de cierre.
        title: t('onboarding.create_connection.steps.done.title'),
        description: t('onboarding.create_connection.steps.done.description'),
      },
    ],
  };
}
